import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs, { Dayjs } from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import PlaceIcon from "@mui/icons-material/Place";
import {
  getColorForLeagueChip,
} from "./TournamentConstants";
import chukkerbookTheme from "src/chukkerbbokTheme";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ScheduledGame {
  scheduled_event_id: number;
  club_id: number;
  tournament_group_id: number;
  tournament_id: number;
  home_team_id: number;
  away_team_id: number;
  game_date: string;
  game_time: string;
  duration_minutes: number;
  field_location: string;
  status: string;
  created_at: string;
}

interface ApprovedTeam {
  team_id: number;
  team_name: string;
  total_handicap: number;
  players: Array<{
    player_id: number;
    first_name: string;
    last_name: string;
    handicap: number;
    position: number;
  }>;
}

interface TournamentInfo {
  tournament_group_id: number;
  tournament_name: string;
  start_date: string;
  end_date: string;
  leagues: Array<{
    tournament_id: number;
    league_name: string;
    handicap: number;
  }>;
}

/* ------------------------------------------------------------------ */
/*  Helper – player roster for one team                                */
/* ------------------------------------------------------------------ */

function TeamRoster({
  team,
  label,
  teamName,
  gradient,
}: {
  team: ApprovedTeam | undefined;
  label: string;
  teamName: string;
  gradient: string;
}) {
  const grouped: Record<number, ApprovedTeam["players"]> = {};
  if (team) {
    team.players.forEach((p) => {
      (grouped[p.position] ??= []).push(p);
    });
  }

  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: gradient,
          px: 2,
          py: 1.5,
        }}
      >
        {label && (
          <Typography
            variant="overline"
            sx={{ color: "rgba(255,255,255,0.7)", letterSpacing: 1.5, fontSize: "0.6rem" }}
          >
            {label}
          </Typography>
        )}
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
          {teamName}
        </Typography>
        {team && (
          <Chip
            label={`HC ${team.total_handicap}`}
            size="small"
            sx={{
              mt: 0.5,
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: "rgba(255,255,255,0.25)",
              color: "#fff",
            }}
          />
        )}
      </Box>

      {/* Players */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {team && team.players.length > 0 ? (
          <Stack spacing={1}>
            {Object.keys(grouped)
              .map(Number)
              .sort((a, b) => a - b)
              .map((pos) => {
                const players = grouped[pos] || [];
                if (players.length === 0) return null;
                return (
                  <Box key={pos}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      #{pos + 1}
                    </Typography>
                    {players.map((player, idx) => (
                      <Stack
                        key={idx}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ pl: 0.5, py: 0.25 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {player.first_name} {player.last_name}
                        </Typography>
                        <Chip
                          label={player.handicap}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            backgroundColor: "action.hover",
                          }}
                        />
                      </Stack>
                    ))}
                  </Box>
                );
              })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
            No players assigned
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function GameDetailsDialog({
  open,
  onClose,
  game,
  teamsMap,
  teamName,
  leagueNameMap,
  tournamentNameMap,
  managerMode,
  devPort,
  clubId,
  loadTournaments,
  tournaments,
  onDelete,
  initialGameDate,
  initialTournamentGroupId,
  allScheduledGames,
  getPlayerIdsOnTeam,
}: {
  open: boolean;
  onClose: () => void;
  game: ScheduledGame | null;
  teamsMap: Record<number, ApprovedTeam>;
  teamName: (id: number) => string;
  leagueNameMap: Record<number, { name: string; groupId: number }>;
  tournamentNameMap: Record<number, string>;
  managerMode: number;
  devPort: string;
  clubId: number;
  loadTournaments: () => void;
  tournaments: TournamentInfo[];
  onDelete: (id: number) => void;
  /** Used only in create mode – the default date for the new game */
  initialGameDate?: string;
  /** Used only in create mode – the pre-selected tournament group */
  initialTournamentGroupId?: number;
  /** All scheduled games – used for conflict detection */
  allScheduledGames?: ScheduledGame[];
  /** Lookup player IDs for a team – used for conflict detection */
  getPlayerIdsOnTeam?: (teamId: number) => number[];
}) {
  const isManager = managerMode === -1;
  const isCreating = game === null;

  /* ---- editing state (all internal) ---- */
  const [isEditing, setIsEditing] = useState(false);
  const [editGameDate, setEditGameDate] = useState<Dayjs | null>(null);
  const [editGameTime, setEditGameTime] = useState<Dayjs | null>(null);
  const [editDurationMinutes, setEditDurationMinutes] = useState(90);
  const [editFieldLocation, setEditFieldLocation] = useState("");
  const [editHomeTeamId, setEditHomeTeamId] = useState(0);
  const [editAwayTeamId, setEditAwayTeamId] = useState(0);
  const [teamsForLeague, setTeamsForLeague] = useState<ApprovedTeam[]>([]);

  /* ---- create-mode state: tournament group & league selection ---- */
  const [editTournamentGroupId, setEditTournamentGroupId] = useState(0);
  const [editLeagueId, setEditLeagueId] = useState(0);

  // Reset edit state whenever the dialog opens
  useEffect(() => {
    if (!open) return;

    if (isCreating) {
      // Create mode – start in edit state with defaults
      const groupId = initialTournamentGroupId || tournaments[0]?.tournament_group_id || 0;
      setEditTournamentGroupId(groupId);
      const tournament = tournaments.find((t) => t.tournament_group_id === groupId);
      const leagueId = tournament?.leagues[0]?.tournament_id || 0;
      setEditLeagueId(leagueId);
      setEditGameDate(initialGameDate ? dayjs(initialGameDate) : dayjs());
      setEditGameTime(dayjs().hour(12).minute(0));
      setEditDurationMinutes(90);
      setEditFieldLocation("");
      setEditHomeTeamId(0);
      setEditAwayTeamId(0);
      setIsEditing(true);
    } else {
      // View mode for existing game
      setEditGameDate(dayjs(game.game_date));
      setEditGameTime(dayjs(`${game.game_date}T${game.game_time}`));
      setEditDurationMinutes(game.duration_minutes);
      setEditFieldLocation(game.field_location || "");
      setEditHomeTeamId(game.home_team_id);
      setEditAwayTeamId(game.away_team_id);
      setEditTournamentGroupId(game.tournament_group_id);
      setEditLeagueId(game.tournament_id);
      setIsEditing(false);
    }
  }, [open, game, isCreating, initialGameDate, initialTournamentGroupId, tournaments]);

  // Fetch approved teams for the relevant league
  const activeTournamentId = isCreating || isEditing ? editLeagueId : game?.tournament_id;
  useEffect(() => {
    if (open && activeTournamentId) {
      fetch(`${devPort}tournament_approved_teams/${clubId}/${activeTournamentId}`)
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data)) setTeamsForLeague(data);
        })
        .catch(console.error);
    } else {
      setTeamsForLeague([]);
    }
  }, [open, activeTournamentId, devPort, clubId]);

  const handleStartEditing = () => {
    if (!game) return;
    setEditGameDate(dayjs(game.game_date));
    setEditGameTime(dayjs(`${game.game_date}T${game.game_time}`));
    setEditDurationMinutes(game.duration_minutes);
    setEditFieldLocation(game.field_location || "");
    setEditHomeTeamId(game.home_team_id);
    setEditAwayTeamId(game.away_team_id);
    setEditTournamentGroupId(game.tournament_group_id);
    setEditLeagueId(game.tournament_id);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (isCreating) {
      onClose();
      return;
    }
    if (!game) return;
    setIsEditing(false);
    setEditGameDate(dayjs(game.game_date));
    setEditGameTime(dayjs(`${game.game_date}T${game.game_time}`));
    setEditDurationMinutes(game.duration_minutes);
    setEditFieldLocation(game.field_location || "");
    setEditHomeTeamId(game.home_team_id);
    setEditAwayTeamId(game.away_team_id);
  };

  const handleSave = () => {
    if (!editGameDate || !editGameTime) return;

    if (isCreating) {
      // ---- CREATE ----
      if (!editHomeTeamId || !editAwayTeamId || !editLeagueId || !editTournamentGroupId) return;
      const payload = {
        club_id: clubId,
        tournament_group_id: editTournamentGroupId,
        tournament_id: editLeagueId,
        home_team_id: editHomeTeamId,
        away_team_id: editAwayTeamId,
        game_date: editGameDate.format("YYYY-MM-DD"),
        game_time: editGameTime.format("HH:mm"),
        duration_minutes: editDurationMinutes,
        field_location: editFieldLocation,
      };

      fetch(`${devPort}tournament_schedule_game`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then(() => {
          loadTournaments();
          onClose();
        })
        .catch(console.error);
    } else {
      // ---- UPDATE ----
      if (!game) return;
      const payload = {
        scheduled_event_id: game.scheduled_event_id,
        club_id: game.club_id,
        tournament_group_id: game.tournament_group_id,
        tournament_id: game.tournament_id,
        home_team_id: editHomeTeamId,
        away_team_id: editAwayTeamId,
        game_date: editGameDate.format("YYYY-MM-DD"),
        game_time: editGameTime.format("HH:mm"),
        duration_minutes: editDurationMinutes,
        field_location: editFieldLocation,
      };

      fetch(`${devPort}tournament_schedule_game_update`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then(() => {
          loadTournaments();
          setIsEditing(false);
        })
        .catch(console.error);
    }
  };

  /* ---- derived data ---- */
  const currentTournamentGroupId = isCreating ? editTournamentGroupId : game!.tournament_group_id;
  const currentTournamentId = isCreating ? editLeagueId : game!.tournament_id;

  const leagueInfo = leagueNameMap[currentTournamentId];
  const tName = tournamentNameMap[currentTournamentGroupId] ?? "Unknown";
  const leagueChipColor = getColorForLeagueChip(currentTournamentId);

  const displayHomeTeamId = isEditing ? editHomeTeamId : (game?.home_team_id ?? 0);
  const displayAwayTeamId = isEditing ? editAwayTeamId : (game?.away_team_id ?? 0);
  const homeTeam = teamsMap[displayHomeTeamId];
  const awayTeam = teamsMap[displayAwayTeamId];

  /* ---- helper: leagues for the selected tournament group (create mode) ---- */
  const leaguesForSelectedGroup =
    tournaments.find((t) => t.tournament_group_id === editTournamentGroupId)?.leagues ?? [];

  /* ---- conflict detection ---- */
  const conflictDetails = useMemo(() => {
    if (!game || !allScheduledGames || !getPlayerIdsOnTeam) return [];

    const gameStart = dayjs(`${game.game_date}T${game.game_time}`);
    const gameEnd = gameStart.add(game.duration_minutes, "minute");

    const thisPlayerIds = [
      ...getPlayerIdsOnTeam(game.home_team_id),
      ...getPlayerIdsOnTeam(game.away_team_id),
    ];
    const thisPlayerIdSet = new Set(thisPlayerIds);

    // Build id → name lookup
    const playerNameById: Record<number, string> = {};
    [game.home_team_id, game.away_team_id].forEach((tid) => {
      const team = teamsMap[tid];
      if (team) team.players.forEach((p) => {
        playerNameById[p.player_id] = `${p.first_name} ${p.last_name}`;
      });
    });

    const conflicts: { playerName: string; otherGame: string }[] = [];

    allScheduledGames.forEach((other) => {
      if (other.scheduled_event_id === game.scheduled_event_id) return;
      if (other.game_date !== game.game_date) return;
      const oStart = dayjs(`${other.game_date}T${other.game_time}`);
      const oEnd = oStart.add(other.duration_minutes, "minute");
      if (!(oStart.isBefore(gameEnd) && gameStart.isBefore(oEnd))) return;

      [other.home_team_id, other.away_team_id].forEach((tid) => {
        const team = teamsMap[tid];
        if (team) team.players.forEach((p) => {
          playerNameById[p.player_id] = `${p.first_name} ${p.last_name}`;
        });
      });

      const otherLabel = `${teamName(other.home_team_id)} vs ${teamName(other.away_team_id)} (${oStart.format("h:mm A")})`;

      const otherPlayerIds = [
        ...getPlayerIdsOnTeam(other.home_team_id),
        ...getPlayerIdsOnTeam(other.away_team_id),
      ];
      otherPlayerIds.forEach((pid) => {
        if (thisPlayerIdSet.has(pid)) {
          conflicts.push({
            playerName: playerNameById[pid] ?? `Player #${pid}`,
            otherGame: otherLabel,
          });
        }
      });
    });

    // Deduplicate
    const seen = new Set<string>();
    return conflicts.filter((c) => {
      const key = `${c.playerName}|${c.otherGame}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [game, allScheduledGames, getPlayerIdsOnTeam, teamsMap, teamName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ---- Coloured header bar ---- */}
      <Box
        sx={{
          background: chukkerbookTheme.primary,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={tName}
              size="small"
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
            {currentTournamentId > 0 && (
              <Chip
                label={leagueInfo?.name ?? `League ${currentTournamentId}`}
                size="small"
                sx={{
                  backgroundColor: leagueChipColor.bg,
                  color: leagueChipColor.text,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Stack>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
            {isCreating
              ? "Schedule New Game"
              : `${teamName(displayHomeTeamId)} \u00A0vs\u00A0 ${teamName(displayAwayTeamId)}`}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.5}>
          {isManager && !isEditing && !isCreating && (
            <>
              <IconButton
                size="small"
                onClick={handleStartEditing}
                sx={{ color: "rgba(255,255,255,0.85)" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  if (game) {
                    onDelete(game.scheduled_event_id);
                    onClose();
                  }
                }}
                sx={{ color: "rgba(255,255,255,0.85)" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={3}>
          {/* ---- Conflict warning banner ---- */}
          {conflictDetails.length > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
              <AlertTitle sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                Player Conflict{conflictDetails.length > 1 ? "s" : ""} Detected
              </AlertTitle>
              {conflictDetails.map((c, i) => (
                <Typography key={i} variant="body2" sx={{ lineHeight: 1.5 }}>
                  • <strong>{c.playerName}</strong> is also playing in{" "}
                  <em>{c.otherGame}</em>
                </Typography>
              ))}
            </Alert>
          )}

          {/* ---- Game info / edit fields ---- */}
          {isEditing ? (
            <Stack spacing={2}>
              {/* Tournament & League selectors – only in create mode */}
              {isCreating && (
                <>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Tournament
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tournament</InputLabel>
                      <Select
                        value={editTournamentGroupId || ""}
                        label="Tournament"
                        onChange={(e) => {
                          const gid = Number(e.target.value);
                          setEditTournamentGroupId(gid);
                          const t = tournaments.find((t) => t.tournament_group_id === gid);
                          setEditLeagueId(t?.leagues[0]?.tournament_id || 0);
                          setEditHomeTeamId(0);
                          setEditAwayTeamId(0);
                        }}
                      >
                        {tournaments.map((t) => (
                          <MenuItem key={t.tournament_group_id} value={t.tournament_group_id}>
                            {t.tournament_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>League</InputLabel>
                      <Select
                        value={editLeagueId || ""}
                        label="League"
                        onChange={(e) => {
                          setEditLeagueId(Number(e.target.value));
                          setEditHomeTeamId(0);
                          setEditAwayTeamId(0);
                        }}
                      >
                        {leaguesForSelectedGroup.map((l) => (
                          <MenuItem key={l.tournament_id} value={l.tournament_id}>
                            {l.league_name} (Handicap: {l.handicap})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Divider />
                </>
              )}

              <Typography variant="subtitle2" fontWeight={600}>
                Game Details
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <DatePicker
                  label="Date"
                  value={editGameDate}
                  onChange={setEditGameDate}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
                <TimePicker
                  label="Time"
                  value={editGameTime}
                  onChange={setEditGameTime}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Duration (min)"
                  type="number"
                  size="small"
                  value={editDurationMinutes}
                  onChange={(e) => setEditDurationMinutes(Math.max(30, Number(e.target.value) || 90))}
                  fullWidth
                  inputProps={{ min: 30, step: 30 }}
                />
                <TextField
                  label="Field / Location"
                  size="small"
                  value={editFieldLocation}
                  onChange={(e) => setEditFieldLocation(e.target.value)}
                  fullWidth
                />
              </Stack>

              <Divider />

              <Typography variant="subtitle2" fontWeight={600}>
                Teams
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Team 1</InputLabel>
                  <Select
                    value={editHomeTeamId || ""}
                    label="Team 1"
                    onChange={(e) => setEditHomeTeamId(Number(e.target.value))}
                  >
                    {teamsForLeague.map((t) => (
                      <MenuItem
                        key={t.team_id}
                        value={t.team_id}
                        disabled={t.team_id === editAwayTeamId}
                      >
                        {t.team_name} (HC: {t.total_handicap})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Team 2</InputLabel>
                  <Select
                    value={editAwayTeamId || ""}
                    label="Team 2"
                    onChange={(e) => setEditAwayTeamId(Number(e.target.value))}
                  >
                    {teamsForLeague.map((t) => (
                      <MenuItem
                        key={t.team_id}
                        value={t.team_id}
                        disabled={t.team_id === editHomeTeamId}
                      >
                        {t.team_name} (HC: {t.total_handicap})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          ) : (
            <>
              {/* Read-only info chips */}
              {game && (
                <Stack
                  direction="row"
                  spacing={3}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    "& > *": {
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    },
                  }}
                >
                  <Box>
                    <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight={500}>
                      {dayjs(`${game.game_date}T${game.game_time}`).format("ddd, MMM D YYYY")}
                    </Typography>
                  </Box>
                  <Box>
                    <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight={500}>
                      {dayjs(`${game.game_date}T${game.game_time}`).format("h:mm A")}
                    </Typography>
                  </Box>
                  <Box>
                    <TimerIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight={500}>
                      {game.duration_minutes} min
                    </Typography>
                  </Box>
                  {game.field_location && (
                    <Box>
                      <PlaceIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" fontWeight={500}>
                        {game.field_location}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </>
          )}

          <Divider />

          {/* ---- Team rosters (only show when at least one team is selected) ---- */}
          {(displayHomeTeamId > 0 || displayAwayTeamId > 0) && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TeamRoster
                team={homeTeam}
                label=""
                teamName={teamName(displayHomeTeamId)}
                gradient={chukkerbookTheme.primary}
              />

              {/* VS badge */}
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 48,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" fontWeight={800} color="text.secondary">
                    VS
                  </Typography>
                </Box>
              </Box>

              <TeamRoster
                team={awayTeam}
                label=""
                teamName={teamName(displayAwayTeamId)}
                gradient={chukkerbookTheme.secondary}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>

      {/* ---- Actions ---- */}
      {isEditing && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelEditing} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !editHomeTeamId ||
              !editAwayTeamId ||
              !editGameDate ||
              !editGameTime ||
              (isCreating && (!editLeagueId || !editTournamentGroupId))
            }
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {isCreating ? "Schedule Game" : "Save Changes"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

